import { createContext, FunctionComponent, h } from 'preact';
import { useCallback, useContext, useEffect, useRef } from 'preact/hooks';
import { useApi } from 'services/use-api';
import { ApplicationContext } from 'services/use-application-setup';
import { uwStatistics } from 'helpers/uw-statistics';
import { configStore } from 'global/config.store';
import { PROFILE_CONFIG } from 'constants-v2/profileConfig';
import { ALLY_FEATURES } from 'constants-v2/ally-features';

const NOTIFY_SERVER_THAT_FEATURE_WAS_TURNED_ON_THROTTLE_TIME = 2 * 60 * 1000; // 2 minutes
const STATISTICS_ENDPOINT = 'v1/stats/enabled-feature';

export const StatisticsContext = createContext<ReturnType<typeof useStatistics>>(null);

export const useStatistics = () => {
  const Api = useApi();
  const { thisSiteId, accountIdCode, isOpen, href } = useContext(ApplicationContext);
  const isOpenedStatisticSent = useRef<boolean>(false);
  const featureWaitList = useRef<string[]>([]);
  const statsRequestWaitList = useRef<string[]>([]);

  const throttleSendingStatRequest = (feature: string, cb: () => void, ms: number) => {
    if (featureWaitList.current.indexOf(feature) !== -1) {
      return;
    }

    cb();

    featureWaitList.current.push(feature);

    setTimeout(() => {
      featureWaitList.current = featureWaitList.current.filter((value) => value !== feature);
    }, ms);
  };

  const sendEnableFeatureStats = (feature: string) => {
    Api.post(STATISTICS_ENDPOINT, {
      siteId: thisSiteId,
      feat: feature,
      account: accountIdCode,
      pageUrl: href,
    }).catch((e) => console.error(e));
  };

  const sendStatistics = (feature: string) => {
    if (thisSiteId) {
      sendEnableFeatureStats(feature);
      return;
    }

    // if client still did not receive config with site id then stats should be sent later
    statsRequestWaitList.current.push(feature);
  };

  useEffect(() => {
    if (thisSiteId && accountIdCode && statsRequestWaitList.current.length) {
      statsRequestWaitList.current.forEach((data) => {
        sendEnableFeatureStats(data);
      });
      statsRequestWaitList.current = [];
    }
  }, [thisSiteId, accountIdCode]);

  const notifyServerThatFeatureWasTurnedOn = (feature: string) => {
    const isProfileFeature = Boolean(PROFILE_CONFIG[feature]);
    const featureName = ALLY_FEATURES[feature].description;

    if (isProfileFeature) {
      uwStatistics.profileTriggered({ data: featureName });
    } else {
      uwStatistics.featureTriggered({ data: featureName });
    }

    throttleSendingStatRequest(
      feature,
      () => {
        sendStatistics(feature);
      },
      NOTIFY_SERVER_THAT_FEATURE_WAS_TURNED_ON_THROTTLE_TIME,
    );
  };

  const notifyServerThatWidgetOpened = useCallback(() => {
    if (isOpenedStatisticSent.current) {
      return;
    }

    uwStatistics.widgetOpened({ data: configStore.config$.lastValue.href });

    sendStatistics('opens');
    isOpenedStatisticSent.current = true;
  }, [thisSiteId, accountIdCode, Api, href]);

  useEffect(() => {
    if (isOpen) {
      notifyServerThatWidgetOpened();
    }
  }, [isOpen, notifyServerThatWidgetOpened]);

  return {
    notifyServerThatFeatureWasTurnedOn,
    sendStatistics,
  };
};

export const StatisticsWrapper: FunctionComponent = ({ children }) => {
  const statistics = useStatistics();

  return <StatisticsContext.Provider value={statistics}>{children}</StatisticsContext.Provider>;
};
